import React from 'react';
import { Outlet, useLocation, type RouteObject } from 'react-router-dom';
import { MsalLoginCallback } from '~/features/authentication/MsalLoginCallback';
import { SecureRoute } from '~/shared/routing/SecureRoute';
import { secureRouteLoader, registerRouteLoader, SECURE_ROUTE_ID } from '~/shared/routing/secureRouteLoader';
import { RedirectToLoginPage } from '~/pages/login/RedirectToLoginPage';
import { RegisterUserFormModal } from '~/libraries/users/RegisterUserFormModal';
import { chartRoutes } from './pages/charts/routes';
import { routes as settingsRoutes } from './pages/settings/routes';
import { routes as schedulingRoutes } from './pages/scheduling/routes';
import { SuspenseWrapper } from './shared/SuspenseWrapper';
import { LogoutPage } from './pages/login/LogoutPage';
import { legacyRoutes } from './features/legacy/routes';
import { AppLayout } from './AppLayout';
import { RouteErrorBoundary } from './shared/RouteErrorBoundary';
import { QaPage } from './pages/qa/QaPage';
import { PlanOfCareReviews } from './features/qa/poc-reviews/PlanOfCareReviews';
import { PlanOfCareReview } from './features/qa/poc-reviews/PlanOfCareReview';
import { VisitPage } from './pages/visits/VisitPage';
import { QaDashboard } from './features/qa/QaDashboard';

const HomePage = React.lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const IntakePage = React.lazy(() => import('./pages/intake/IntakePage').then(m => ({ default: m.IntakePage })));
const BillingPage = React.lazy(() => import('./features/billing/dashboard/BillingPage').then(m => ({ default: m.BillingPage })));
const PatientsTable = React.lazy(() => import('./features/intake/patients/PatientsTable').then(m => ({ default: m.PatientsTable })));
const OasisPage = React.lazy(() => import('./pages/oasis/OasisPage').then(m => ({ default: m.OasisPage })));
const PhysicianOrdersPage = React.lazy(() =>
	import('./features/orders/signatures/PhysicianOrdersPage').then(m => ({ default: m.PhysicianOrdersPage }))
);

function MainOutlet() {
	const { pathname } = useLocation();
	const section = pathname.split('/')[1] ?? '';
	return (
		<SuspenseWrapper key={section} name="MainOutlet">
			<Outlet />
		</SuspenseWrapper>
	);
}

export const rootRoutes: RouteObject[] = [
	{ path: '/login/callback', Component: MsalLoginCallback },
	{ path: '/login', Component: RedirectToLoginPage },
	{ path: 'logout', Component: LogoutPage },
	{
		path: '/register',
		loader: registerRouteLoader,
		element: <RegisterUserFormModal />,
		errorElement: <RouteErrorBoundary />
	},
	{
		id: SECURE_ROUTE_ID,
		element: <SecureRoute />,
		loader: secureRouteLoader,
		errorElement: <RouteErrorBoundary />,
		shouldRevalidate: () => false,
		children: [
			{
				element: <AppLayout />,
				errorElement: <RouteErrorBoundary />,
				children: [
					{
						element: <MainOutlet />,
						children: [
							{ path: '/', element: <HomePage />, handle: { menuLabel: 'Home', disableBreadCrumb: true, disableBack: true } },
							{
								path: '/referrals',
								element: <IntakePage />,
								handle: { menuLabel: 'Intake', disableBreadCrumb: true, disableBack: true }
							},
							schedulingRoutes,
							{ path: '/patients', element: <PatientsTable /> },
							{ path: '/orders', element: <PhysicianOrdersPage />, handle: { menuLabel: 'Orders', disableBreadCrumb: true } },
							{
								path: '/qa',
								handle: { menuLabel: 'QA', disableBreadCrumb: true, disableBack: true },
								element: <QaPage />,
								children: [
									{ index: true, element: <QaDashboard /> },
									{ path: 'poc-reviews', element: <PlanOfCareReviews /> },
									{
										path: 'poc-reviews/:reviewId',
										element: (
											<SuspenseWrapper name="PlanOfCareReview">
												<PlanOfCareReview />
											</SuspenseWrapper>
										)
									}
								]
							},
							{ path: '/oasis', element: <OasisPage />, handle: { menuLabel: 'OASIS', disableBreadCrumb: true, disableBack: true } },
							{ path: '/billing', element: <BillingPage /> },
							...settingsRoutes,
							...legacyRoutes
						]
					},
					chartRoutes,
					{
						path: '/visits/:stayId/:visitId/:selectedBucketId?/:selectedBucketItemId?',
						handle: {
							disableBreadCrumb: true
						},
						element: <VisitPage />
					}
				]
			}
		]
	}
];
